import { __assign, __awaiter, __extends, __generator } from "tslib";
import { WebXRAbstractFeature } from "./WebXRAbstractFeature";
import { WebXRFeatureName } from "../webXRFeaturesManager";
import { SphereBuilder } from "../../Meshes/Builders/sphereBuilder";
import { Quaternion } from "../../Maths/math.vector";
import { PhysicsImpostor } from "../../Physics/physicsImpostor";
import { WebXRFeaturesManager } from "../webXRFeaturesManager";
import { Observable } from "../../Misc/observable";
import { SceneLoader } from "../../Loading/sceneLoader";
import { Color3 } from "../../Maths/math.color";
import { NodeMaterial } from "../../Materials/Node/nodeMaterial";
import { Material } from "../../Materials/material";
import { Engine } from "../../Engines/engine";
import { Tools } from "../../Misc/tools";
import { Axis } from "../../Maths/math.axis";
import { Tags } from "../../Misc/tags";
var XRHandJoint;
(function (XRHandJoint) {
    XRHandJoint["wrist"] = "wrist";
    XRHandJoint["thumb-metacarpal"] = "thumb-metacarpal";
    XRHandJoint["thumb-phalanx-proximal"] = "thumb-phalanx-proximal";
    XRHandJoint["thumb-phalanx-distal"] = "thumb-phalanx-distal";
    XRHandJoint["thumb-tip"] = "thumb-tip";
    XRHandJoint["index-finger-metacarpal"] = "index-finger-metacarpal";
    XRHandJoint["index-finger-phalanx-proximal"] = "index-finger-phalanx-proximal";
    XRHandJoint["index-finger-phalanx-intermediate"] = "index-finger-phalanx-intermediate";
    XRHandJoint["index-finger-phalanx-distal"] = "index-finger-phalanx-distal";
    XRHandJoint["index-finger-tip"] = "index-finger-tip";
    XRHandJoint["middle-finger-metacarpal"] = "middle-finger-metacarpal";
    XRHandJoint["middle-finger-phalanx-proximal"] = "middle-finger-phalanx-proximal";
    XRHandJoint["middle-finger-phalanx-intermediate"] = "middle-finger-phalanx-intermediate";
    XRHandJoint["middle-finger-phalanx-distal"] = "middle-finger-phalanx-distal";
    XRHandJoint["middle-finger-tip"] = "middle-finger-tip";
    XRHandJoint["ring-finger-metacarpal"] = "ring-finger-metacarpal";
    XRHandJoint["ring-finger-phalanx-proximal"] = "ring-finger-phalanx-proximal";
    XRHandJoint["ring-finger-phalanx-intermediate"] = "ring-finger-phalanx-intermediate";
    XRHandJoint["ring-finger-phalanx-distal"] = "ring-finger-phalanx-distal";
    XRHandJoint["ring-finger-tip"] = "ring-finger-tip";
    XRHandJoint["pinky-finger-metacarpal"] = "pinky-finger-metacarpal";
    XRHandJoint["pinky-finger-phalanx-proximal"] = "pinky-finger-phalanx-proximal";
    XRHandJoint["pinky-finger-phalanx-intermediate"] = "pinky-finger-phalanx-intermediate";
    XRHandJoint["pinky-finger-phalanx-distal"] = "pinky-finger-phalanx-distal";
    XRHandJoint["pinky-finger-tip"] = "pinky-finger-tip";
})(XRHandJoint || (XRHandJoint = {}));
var handJointReferenceArray = [
    XRHandJoint.wrist,
    XRHandJoint["thumb-metacarpal"],
    XRHandJoint["thumb-phalanx-proximal"],
    XRHandJoint["thumb-phalanx-distal"],
    XRHandJoint["thumb-tip"],
    XRHandJoint["index-finger-metacarpal"],
    XRHandJoint["index-finger-phalanx-proximal"],
    XRHandJoint["index-finger-phalanx-intermediate"],
    XRHandJoint["index-finger-phalanx-distal"],
    XRHandJoint["index-finger-tip"],
    XRHandJoint["middle-finger-metacarpal"],
    XRHandJoint["middle-finger-phalanx-proximal"],
    XRHandJoint["middle-finger-phalanx-intermediate"],
    XRHandJoint["middle-finger-phalanx-distal"],
    XRHandJoint["middle-finger-tip"],
    XRHandJoint["ring-finger-metacarpal"],
    XRHandJoint["ring-finger-phalanx-proximal"],
    XRHandJoint["ring-finger-phalanx-intermediate"],
    XRHandJoint["ring-finger-phalanx-distal"],
    XRHandJoint["ring-finger-tip"],
    XRHandJoint["pinky-finger-metacarpal"],
    XRHandJoint["pinky-finger-phalanx-proximal"],
    XRHandJoint["pinky-finger-phalanx-intermediate"],
    XRHandJoint["pinky-finger-phalanx-distal"],
    XRHandJoint["pinky-finger-tip"],
];
/**
 * Representing a single hand (with its corresponding native XRHand object)
 */
var WebXRHand = /** @class */ (function () {
    /**
     * Construct a new hand object
     * @param xrController the controller to which the hand correlates
     * @param trackedMeshes the meshes to be used to track the hand joints
     * @param _handMesh an optional hand mesh. if not provided, ours will be used
     * @param _rigMapping an optional rig mapping for the hand mesh. if not provided, ours will be used
     * @param disableDefaultHandMesh should the default mesh creation be disabled
     * @param _nearInteractionMesh as optional mesh used for near interaction collision checking
     * @param _leftHandedMeshes are the hand meshes left-handed-system meshes
     */
    function WebXRHand(
    /** the controller to which the hand correlates */
    xrController, 
    /** the meshes to be used to track the hand joints */
    trackedMeshes, _handMesh, _rigMapping, disableDefaultHandMesh, _nearInteractionMesh, _leftHandedMeshes) {
        var _this = this;
        this.xrController = xrController;
        this.trackedMeshes = trackedMeshes;
        this._handMesh = _handMesh;
        this._rigMapping = _rigMapping;
        this._nearInteractionMesh = _nearInteractionMesh;
        this._leftHandedMeshes = _leftHandedMeshes;
        this._defaultHandMesh = false;
        this._transformNodeMapping = [];
        this._boneMapping = [];
        this._useBones = false;
        /**
         * Observers will be triggered when the mesh for this hand was initialized.
         */
        this.onHandMeshReadyObservable = new Observable();
        this.handPartsDefinition = this.generateHandPartsDefinition();
        this._scene = trackedMeshes.get("wrist").getScene();
        this.onHandMeshReadyObservable.add(function () {
            // check if we should use bones or transform nodes
            if (!_this._rigMapping || !_this._handMesh) {
                return;
            }
            var transformNode = _this._scene.getTransformNodeByName(_this._rigMapping[0]);
            if (!transformNode) {
                var bone = _this._scene.getBoneByName(_this._rigMapping[0]);
                if (bone) {
                    _this._useBones = true;
                }
            }
            _this._handMesh.alwaysSelectAsActiveMesh = true;
            _this._handMesh.getChildMeshes().forEach(function (m) { return (m.alwaysSelectAsActiveMesh = true); });
        });
        if (this._handMesh && this._rigMapping) {
            this._defaultHandMesh = false;
            this.onHandMeshReadyObservable.notifyObservers(this);
        }
        else {
            if (!disableDefaultHandMesh) {
                this._generateDefaultHandMesh();
            }
        }
        // hide the motion controller, if available/loaded
        if (this.xrController.motionController) {
            if (this.xrController.motionController.rootMesh) {
                this.xrController.motionController.rootMesh.setEnabled(false);
            }
            else {
                this.xrController.motionController.onModelLoadedObservable.add(function (controller) {
                    if (controller.rootMesh) {
                        controller.rootMesh.setEnabled(false);
                    }
                });
            }
        }
        this.xrController.onMotionControllerInitObservable.add(function (motionController) {
            motionController.onModelLoadedObservable.add(function (controller) {
                if (controller.rootMesh) {
                    controller.rootMesh.setEnabled(false);
                }
            });
            if (motionController.rootMesh) {
                motionController.rootMesh.setEnabled(false);
            }
        });
    }
    /**
     * Populate the HandPartsDefinition object.
     * This is called as a side effect since certain browsers don't have XRHand defined.
     */
    WebXRHand.prototype.generateHandPartsDefinition = function () {
        var _a;
        return _a = {},
            _a["wrist" /* WRIST */] = [XRHandJoint.wrist],
            _a["thumb" /* THUMB */] = [XRHandJoint["thumb-metacarpal"], XRHandJoint["thumb-phalanx-proximal"], XRHandJoint["thumb-phalanx-distal"], XRHandJoint["thumb-tip"]],
            _a["index" /* INDEX */] = [
                XRHandJoint["index-finger-metacarpal"],
                XRHandJoint["index-finger-phalanx-proximal"],
                XRHandJoint["index-finger-phalanx-intermediate"],
                XRHandJoint["index-finger-phalanx-distal"],
                XRHandJoint["index-finger-tip"],
            ],
            _a["middle" /* MIDDLE */] = [
                XRHandJoint["middle-finger-metacarpal"],
                XRHandJoint["middle-finger-phalanx-proximal"],
                XRHandJoint["middle-finger-phalanx-intermediate"],
                XRHandJoint["middle-finger-phalanx-distal"],
                XRHandJoint["middle-finger-tip"],
            ],
            _a["ring" /* RING */] = [
                XRHandJoint["ring-finger-metacarpal"],
                XRHandJoint["ring-finger-phalanx-proximal"],
                XRHandJoint["ring-finger-phalanx-intermediate"],
                XRHandJoint["ring-finger-phalanx-distal"],
                XRHandJoint["ring-finger-tip"],
            ],
            _a["little" /* LITTLE */] = [
                XRHandJoint["pinky-finger-metacarpal"],
                XRHandJoint["pinky-finger-phalanx-proximal"],
                XRHandJoint["pinky-finger-phalanx-intermediate"],
                XRHandJoint["pinky-finger-phalanx-distal"],
                XRHandJoint["pinky-finger-tip"],
            ],
            _a;
    };
    Object.defineProperty(WebXRHand.prototype, "handMesh", {
        /**
         * Get the hand mesh. It is possible that the hand mesh is not yet ready!
         */
        get: function () {
            return this._handMesh;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Update this hand from the latest xr frame
     * @param xrFrame xrFrame to update from
     * @param referenceSpace The current viewer reference space
     * @param scaleFactor optional scale factor for the meshes
     */
    WebXRHand.prototype.updateFromXRFrame = function (xrFrame, referenceSpace, scaleFactor) {
        var _this = this;
        if (scaleFactor === void 0) { scaleFactor = 2; }
        var hand = this.xrController.inputSource.hand;
        if (!hand) {
            return;
        }
        this.trackedMeshes.forEach(function (mesh, name) {
            var xrJoint = hand.get(name);
            if (xrJoint) {
                var pose = xrFrame.getJointPose(xrJoint, referenceSpace);
                if (!pose || !pose.transform) {
                    return;
                }
                // get the transformation. can be done with matrix decomposition as well
                var pos = pose.transform.position;
                var orientation_1 = pose.transform.orientation;
                mesh.position.set(pos.x, pos.y, pos.z);
                mesh.rotationQuaternion.set(orientation_1.x, orientation_1.y, orientation_1.z, orientation_1.w);
                // left handed system conversion
                // get the radius of the joint. In general it is static, but just in case it does change we update it on each frame.
                var radius = (pose.radius || 0.008) * scaleFactor;
                mesh.scaling.set(radius, radius, radius);
                // if we are using left-handed meshes, transform before applying to the hand mesh
                if (_this._leftHandedMeshes && !mesh.getScene().useRightHandedSystem) {
                    mesh.position.z *= -1;
                    mesh.rotationQuaternion.z *= -1;
                    mesh.rotationQuaternion.w *= -1;
                }
                // now check for the hand mesh
                if (_this._handMesh && _this._rigMapping) {
                    var idx = handJointReferenceArray.indexOf(name);
                    if (_this._rigMapping[idx]) {
                        if (_this._useBones) {
                            _this._boneMapping[idx] = _this._boneMapping[idx] || _this._scene.getBoneByName(_this._rigMapping[idx]);
                            if (_this._boneMapping[idx]) {
                                _this._boneMapping[idx].setPosition(mesh.position);
                                _this._boneMapping[idx].setRotationQuaternion(mesh.rotationQuaternion);
                                mesh.isVisible = false;
                            }
                        }
                        else {
                            _this._transformNodeMapping[idx] = _this._transformNodeMapping[idx] || _this._scene.getTransformNodeByName(_this._rigMapping[idx]);
                            if (_this._transformNodeMapping[idx]) {
                                _this._transformNodeMapping[idx].position.copyFrom(mesh.position);
                                _this._transformNodeMapping[idx].rotationQuaternion.copyFrom(mesh.rotationQuaternion);
                                // no scaling at the moment
                                // this._transformNodeMapping[idx].scaling.copyFrom(mesh.scaling).scaleInPlace(20);
                                mesh.isVisible = false;
                            }
                        }
                    }
                }
                if (!_this._leftHandedMeshes && !mesh.getScene().useRightHandedSystem) {
                    mesh.position.z *= -1;
                    mesh.rotationQuaternion.z *= -1;
                    mesh.rotationQuaternion.w *= -1;
                }
            }
        });
        // Update the invisible fingertip collidable
        if (this._nearInteractionMesh && this.trackedMeshes.has("index-finger-tip")) {
            var indexTipPose = this.trackedMeshes.get("index-finger-tip").position;
            this._nearInteractionMesh.position.set(indexTipPose.x, indexTipPose.y, indexTipPose.z);
        }
    };
    /**
     * Get meshes of part of the hand
     * @param part the part of hand to get
     * @returns An array of meshes that correlate to the hand part requested
     */
    WebXRHand.prototype.getHandPartMeshes = function (part) {
        var _this = this;
        return this.handPartsDefinition[part].map(function (name) { return _this.trackedMeshes.get(name); });
    };
    /**
     * Dispose this Hand object
     */
    WebXRHand.prototype.dispose = function () {
        var _a;
        this.trackedMeshes.forEach(function (mesh) { return mesh.dispose(true); }); // spare anything which might have been parented to one
        (_a = this._nearInteractionMesh) === null || _a === void 0 ? void 0 : _a.dispose();
        this.onHandMeshReadyObservable.clear();
        // dispose the hand mesh, if it is the default one
        if (this._defaultHandMesh && this._handMesh) {
            this._handMesh.dispose();
        }
    };
    WebXRHand.prototype._generateDefaultHandMesh = function () {
        return __awaiter(this, void 0, void 0, function () {
            var handedness_1, filename, loaded, handColors, handShader, handNodes, tm, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, , 4]);
                        handedness_1 = this.xrController.inputSource.handedness === "right" ? "right" : "left";
                        filename = (handedness_1 === "right" ? "r" : "l") + "_hand_rhs.glb";
                        return [4 /*yield*/, SceneLoader.ImportMeshAsync("", "https://assets.babylonjs.com/meshes/HandMeshes/", filename, this._scene)];
                    case 1:
                        loaded = _a.sent();
                        handColors = {
                            base: Color3.FromInts(116, 63, 203),
                            fresnel: Color3.FromInts(149, 102, 229),
                            fingerColor: Color3.FromInts(177, 130, 255),
                            tipFresnel: Color3.FromInts(220, 200, 255),
                        };
                        handShader = new NodeMaterial("leftHandShader", this._scene, { emitComments: false });
                        return [4 /*yield*/, handShader.loadAsync("https://assets.babylonjs.com/meshes/HandMeshes/handsShader.json")];
                    case 2:
                        _a.sent();
                        // build node materials
                        handShader.build(false);
                        // depth prepass and alpha mode
                        handShader.needDepthPrePass = true;
                        handShader.transparencyMode = Material.MATERIAL_ALPHABLEND;
                        handShader.alphaMode = Engine.ALPHA_COMBINE;
                        handNodes = {
                            base: handShader.getBlockByName("baseColor"),
                            fresnel: handShader.getBlockByName("fresnelColor"),
                            fingerColor: handShader.getBlockByName("fingerColor"),
                            tipFresnel: handShader.getBlockByName("tipFresnelColor"),
                        };
                        handNodes.base.value = handColors.base;
                        handNodes.fresnel.value = handColors.fresnel;
                        handNodes.fingerColor.value = handColors.fingerColor;
                        handNodes.tipFresnel.value = handColors.tipFresnel;
                        loaded.meshes[1].material = handShader;
                        loaded.meshes[1].alwaysSelectAsActiveMesh = true;
                        this._defaultHandMesh = true;
                        this._handMesh = loaded.meshes[0];
                        this._rigMapping = [
                            "wrist_",
                            "thumb_metacarpal_",
                            "thumb_proxPhalanx_",
                            "thumb_distPhalanx_",
                            "thumb_tip_",
                            "index_metacarpal_",
                            "index_proxPhalanx_",
                            "index_intPhalanx_",
                            "index_distPhalanx_",
                            "index_tip_",
                            "middle_metacarpal_",
                            "middle_proxPhalanx_",
                            "middle_intPhalanx_",
                            "middle_distPhalanx_",
                            "middle_tip_",
                            "ring_metacarpal_",
                            "ring_proxPhalanx_",
                            "ring_intPhalanx_",
                            "ring_distPhalanx_",
                            "ring_tip_",
                            "little_metacarpal_",
                            "little_proxPhalanx_",
                            "little_intPhalanx_",
                            "little_distPhalanx_",
                            "little_tip_",
                        ].map(function (joint) { return "" + joint + (handedness_1 === "right" ? "R" : "L"); });
                        // single change for left handed systems
                        if (!this._scene.useRightHandedSystem) {
                            tm = this._scene.getTransformNodeByName(this._rigMapping[0]);
                            if (!tm) {
                                throw new Error("could not find the wrist node");
                            }
                            else {
                                tm.parent && tm.parent.rotate(Axis.Y, Math.PI);
                            }
                        }
                        this.onHandMeshReadyObservable.notifyObservers(this);
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        Tools.Error("error loading hand mesh");
                        console.log(e_1);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return WebXRHand;
}());
export { WebXRHand };
/**
 * WebXR Hand Joint tracking feature, available for selected browsers and devices
 */
var WebXRHandTracking = /** @class */ (function (_super) {
    __extends(WebXRHandTracking, _super);
    /**
     * Creates a new instance of the hit test feature
     * @param _xrSessionManager an instance of WebXRSessionManager
     * @param options options to use when constructing this feature
     */
    function WebXRHandTracking(_xrSessionManager, 
    /**
     * options to use when constructing this feature
     */
    options) {
        var _this = _super.call(this, _xrSessionManager) || this;
        _this.options = options;
        /**
         * This observable will notify registered observers when a new hand object was added and initialized
         */
        _this.onHandAddedObservable = new Observable();
        /**
         * This observable will notify its observers right before the hand object is disposed
         */
        _this.onHandRemovedObservable = new Observable();
        _this._hands = {};
        _this._attachHand = function (xrController) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
            if (!xrController.inputSource.hand || _this._hands[xrController.uniqueId]) {
                // already attached
                return;
            }
            var hand = xrController.inputSource.hand;
            var trackedMeshes = new Map();
            var originalMesh = ((_a = _this.options.jointMeshes) === null || _a === void 0 ? void 0 : _a.sourceMesh) || SphereBuilder.CreateSphere("jointParent", { diameter: 1 });
            originalMesh.scaling.set(0.01, 0.01, 0.01);
            originalMesh.isVisible = !!((_b = _this.options.jointMeshes) === null || _b === void 0 ? void 0 : _b.keepOriginalVisible);
            for (var i = 0; i < hand.size; ++i) {
                var newInstance = originalMesh.createInstance(xrController.uniqueId + "-handJoint-" + i);
                if ((_c = _this.options.jointMeshes) === null || _c === void 0 ? void 0 : _c.onHandJointMeshGenerated) {
                    var returnedMesh = _this.options.jointMeshes.onHandJointMeshGenerated(newInstance, i, xrController.uniqueId);
                    if (returnedMesh) {
                        if (returnedMesh !== newInstance) {
                            newInstance.dispose();
                            newInstance = returnedMesh;
                        }
                    }
                }
                newInstance.isPickable = false;
                if ((_d = _this.options.jointMeshes) === null || _d === void 0 ? void 0 : _d.enablePhysics) {
                    var props = _this.options.jointMeshes.physicsProps || {};
                    var type = props.impostorType !== undefined ? props.impostorType : PhysicsImpostor.SphereImpostor;
                    newInstance.physicsImpostor = new PhysicsImpostor(newInstance, type, __assign({ mass: 0 }, props));
                }
                newInstance.rotationQuaternion = new Quaternion();
                if ((_e = _this.options.jointMeshes) === null || _e === void 0 ? void 0 : _e.invisible) {
                    newInstance.isVisible = false;
                }
                trackedMeshes.set(handJointReferenceArray[i], newInstance);
            }
            var touchMesh = null;
            if ((_f = _this.options.jointMeshes) === null || _f === void 0 ? void 0 : _f.sceneForNearInteraction) {
                touchMesh = SphereBuilder.CreateSphere(xrController.uniqueId + "-handJoint-indexCollidable", {}, _this.options.jointMeshes.sceneForNearInteraction);
                touchMesh.isVisible = false;
                Tags.AddTagsTo(touchMesh, "touchEnabled");
            }
            var handedness = xrController.inputSource.handedness === "right" ? "right" : "left";
            var handMesh = ((_g = _this.options.jointMeshes) === null || _g === void 0 ? void 0 : _g.handMeshes) && ((_h = _this.options.jointMeshes) === null || _h === void 0 ? void 0 : _h.handMeshes[handedness]);
            var rigMapping = ((_j = _this.options.jointMeshes) === null || _j === void 0 ? void 0 : _j.rigMapping) && ((_k = _this.options.jointMeshes) === null || _k === void 0 ? void 0 : _k.rigMapping[handedness]);
            var webxrHand = new WebXRHand(xrController, trackedMeshes, handMesh, rigMapping, (_l = _this.options.jointMeshes) === null || _l === void 0 ? void 0 : _l.disableDefaultHandMesh, touchMesh, (_m = _this.options.jointMeshes) === null || _m === void 0 ? void 0 : _m.leftHandedSystemMeshes);
            // get two new meshes
            _this._hands[xrController.uniqueId] = {
                handObject: webxrHand,
                id: WebXRHandTracking._idCounter++,
            };
            _this.onHandAddedObservable.notifyObservers(webxrHand);
        };
        _this.xrNativeFeatureName = "hand-tracking";
        return _this;
    }
    /**
     * Check if the needed objects are defined.
     * This does not mean that the feature is enabled, but that the objects needed are well defined.
     */
    WebXRHandTracking.prototype.isCompatible = function () {
        return typeof XRHand !== "undefined";
    };
    /**
     * attach this feature
     * Will usually be called by the features manager
     *
     * @returns true if successful.
     */
    WebXRHandTracking.prototype.attach = function () {
        var _this = this;
        if (!_super.prototype.attach.call(this)) {
            return false;
        }
        this.options.xrInput.controllers.forEach(this._attachHand);
        this._addNewAttachObserver(this.options.xrInput.onControllerAddedObservable, this._attachHand);
        this._addNewAttachObserver(this.options.xrInput.onControllerRemovedObservable, function (controller) {
            // REMOVE the controller
            _this._detachHand(controller.uniqueId);
        });
        return true;
    };
    /**
     * detach this feature.
     * Will usually be called by the features manager
     *
     * @returns true if successful.
     */
    WebXRHandTracking.prototype.detach = function () {
        var _this = this;
        if (!_super.prototype.detach.call(this)) {
            return false;
        }
        Object.keys(this._hands).forEach(function (controllerId) {
            _this._detachHand(controllerId);
        });
        return true;
    };
    /**
     * Dispose this feature and all of the resources attached
     */
    WebXRHandTracking.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        this.onHandAddedObservable.clear();
    };
    /**
     * Get the hand object according to the controller id
     * @param controllerId the controller id to which we want to get the hand
     * @returns null if not found or the WebXRHand object if found
     */
    WebXRHandTracking.prototype.getHandByControllerId = function (controllerId) {
        var _a;
        return ((_a = this._hands[controllerId]) === null || _a === void 0 ? void 0 : _a.handObject) || null;
    };
    /**
     * Get a hand object according to the requested handedness
     * @param handedness the handedness to request
     * @returns null if not found or the WebXRHand object if found
     */
    WebXRHandTracking.prototype.getHandByHandedness = function (handedness) {
        var found = null;
        for (var key in this._hands) {
            if (this._hands[key].handObject.xrController.inputSource.handedness === handedness) {
                found = this._hands[key];
            }
        }
        if (found) {
            return found.handObject;
        }
        return null;
    };
    WebXRHandTracking.prototype._onXRFrame = function (_xrFrame) {
        var _this = this;
        // iterate over the hands object
        Object.keys(this._hands).forEach(function (id) {
            var _a;
            _this._hands[id].handObject.updateFromXRFrame(_xrFrame, _this._xrSessionManager.referenceSpace, (_a = _this.options.jointMeshes) === null || _a === void 0 ? void 0 : _a.scaleFactor);
        });
    };
    WebXRHandTracking.prototype._detachHand = function (controllerId) {
        if (this._hands[controllerId]) {
            this.onHandRemovedObservable.notifyObservers(this._hands[controllerId].handObject);
            this._hands[controllerId].handObject.dispose();
            delete this._hands[controllerId];
        }
    };
    WebXRHandTracking._idCounter = 0;
    /**
     * The module's name
     */
    WebXRHandTracking.Name = WebXRFeatureName.HAND_TRACKING;
    /**
     * The (Babylon) version of this module.
     * This is an integer representing the implementation version.
     * This number does not correspond to the WebXR specs version
     */
    WebXRHandTracking.Version = 1;
    return WebXRHandTracking;
}(WebXRAbstractFeature));
export { WebXRHandTracking };
//register the plugin
WebXRFeaturesManager.AddWebXRFeature(WebXRHandTracking.Name, function (xrSessionManager, options) {
    return function () { return new WebXRHandTracking(xrSessionManager, options); };
}, WebXRHandTracking.Version, false);
//# sourceMappingURL=WebXRHandTracking.js.map